import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Heart, Play } from 'lucide-react';
import type { Game } from '../../shared/types';
import { usePlatform } from '../store';
import { gameTitle } from '../game-presentation';
import { gameDestination } from '../island-world';
import { AtlasArt as GameArt } from './AtlasArt';
import { ATLAS_CARD_SIZES, ATLAS_FEATURED_SIZES } from '../../shared/image-delivery';
import { GameReleaseBadge, GameReleaseTime } from './GameRelease';

export function AtlasGameCard({
  game,
  featured = false,
  showRelease = false,
  sizes,
}: {
  game: Game;
  featured?: boolean;
  showRelease?: boolean;
  sizes?: string;
}) {
  const { data, toggleFavorite } = usePlatform();
  const favorite = data?.favorites.includes(game.id) || false;
  const allowed = game.free || data?.user?.member;
  const release = showRelease ? game.latestRelease : undefined;
  return (
    <article className={`atlas-game-card${featured ? ' atlas-featured-card' : ''}`}>
      <Link
        className="atlas-card-launch"
        to={gameDestination(game, !!data?.user?.member)}
        aria-label={`${allowed ? '开始玩' : '查看详情'}${gameTitle(game)}`}
      >
        <GameArt
          game={game}
          sizes={sizes ?? (featured ? ATLAS_FEATURED_SIZES : ATLAS_CARD_SIZES)}
        />
        {release && <GameReleaseBadge release={release} />}
        {featured ? (
          <div className="atlas-featured-copy">
            <h3>{gameTitle(game)}</h3>
            <p>{game.categories.slice(0, 2).join(' · ')}</p>
            <span className="atlas-featured-play">
              {allowed ? '立即开玩' : '查看详情'}
              <ChevronRight size={17} />
            </span>
          </div>
        ) : (
          <span className="atlas-card-hover">
            <Play size={18} fill="currentColor" />
            {allowed ? '即刻开玩' : '查看详情'}
          </span>
        )}
        {!game.free && <span className="atlas-card-member">会员</span>}
      </Link>
      <div className="atlas-card-caption">
        {!featured && (
          <Link className="atlas-card-name" to={gameDestination(game, !!data?.user?.member)}>
            <h3>{gameTitle(game)}</h3>
            <small className={release ? 'latest-game-meta' : undefined}>
              <span>{game.categories[0]}</span>
              {release && <GameReleaseTime release={release} />}
            </small>
          </Link>
        )}
        <Link
          className="atlas-card-detail"
          to={'/game/' + game.slug}
          aria-label={`${gameTitle(game)}详情`}
        >
          <ArrowUpRight size={14} />
        </Link>
        <button
          className={favorite ? 'selected' : ''}
          aria-label={`${favorite ? '取消收藏' : '收藏'}${gameTitle(game)}`}
          aria-pressed={favorite}
          onClick={() => void toggleFavorite(game)}
        >
          <Heart size={15} fill={favorite ? 'currentColor' : 'none'} />
        </button>
      </div>
    </article>
  );
}
